import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBuilding,
  FaFileContract,
  FaClock,
  FaCreditCard,
  FaChartLine,
  FaSync,
  FaExclamationTriangle,
  FaChartBar,
  FaPlus,
  FaCheck,
  FaBell,
  FaSearch,
  FaFilter,
  FaUmbrellaBeach,
  FaUserFriends,
} from 'react-icons/fa';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const CorporateOverview = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('month');
  const [viewType, setViewType] = useState('corporate'); // 'corporate' or 'leisure'
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [topCompanies, setTopCompanies] = useState([]);

  // API Base URL - adjust according to your backend
  const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

  // Fetch all data when component mounts or when timeRange/viewType changes
  useEffect(() => {
    fetchAllData();
  }, [timeRange, viewType]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      if (viewType === 'corporate') {
        await Promise.all([
          fetchOverviewData(),
          fetchRevenueData(),
          fetchRecentActivities(),
          fetchTopCompanies(),
        ]);
      } else {
        // For leisure view, you can implement similar APIs
        // For now, we'll use corporate data as fallback
        await Promise.all([
          fetchOverviewData(),
          fetchRevenueData(),
          fetchRecentActivities(),
          fetchTopCompanies(),
        ]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverviewData = async () => {
    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/corporate-pricing/overview?time_range=${timeRange}`
      );
      const data = await response.json();
      if (data.success) {
        setOverviewData(data.data.overview);
      }
    } catch (error) {
      console.error('Error fetching overview:', error);
    }
  };

  const fetchRevenueData = async () => {
    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/corporate-pricing/revenue-data?time_range=${timeRange}`
      );
      const data = await response.json();
      if (data.success) {
        setRevenueData(data.data);
      }
    } catch (error) {
      console.error('Error fetching revenue data:', error);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      const response = await fetch(
        `${BQ_BASE_URL}bq/api/corporate-pricing/recent-activities?limit=10`
      );
      const data = await response.json();
      if (data.success) {
        setRecentActivities(data.data);
      }
    } catch (error) {
      console.error('Error fetching recent activities:', error);
    }
  };

  const fetchTopCompanies = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/corporate-pricing/top-companies?limit=5`);
      const data = await response.json();
      if (data.success) {
        setTopCompanies(data.data);
      }
    } catch (error) {
      console.error('Error fetching top companies:', error);
    }
  };

  // Handle time range change
  const handleTimeRangeChange = (newTimeRange) => {
    setTimeRange(newTimeRange);
  };

  // Handle view type change
  const handleViewTypeChange = (newViewType) => {
    setViewType(newViewType);
  };

  // Loading component
  if (loading && !overviewData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Use real data from backend or fallback to empty states
  const dashboardStats = overviewData || {
    totalCompanies: 0,
    activeContracts: 0,
    pendingVerifications: 0,
    totalRevenue: '₹0',
    averageSpending: '₹0',
    renewalThisMonth: 0,
    highRiskAccounts: 0,
  };

  // Chart data - use real data from backend
  const chartData = revenueData.length > 0 ? revenueData : [];

  // Recent activities - use real data from backend
  const activities = recentActivities.length > 0 ? recentActivities : [];

  // Top items based on view type
  const topItems = viewType === 'corporate' ? topCompanies : [];

  const StatCard = ({ title, value, change, icon, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      yellow: 'bg-yellow-50 text-yellow-600',
      purple: 'bg-purple-50 text-purple-600',
      orange: 'bg-orange-50 text-orange-600',
      red: 'bg-red-50 text-red-600',
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow duration-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">{title}</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
            {change && (
              <p
                className={`text-xs mt-1 ${change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}
              >
                {change} from last period
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ActivityItem = ({ activity }) => (
    <div className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200">
      <div
        className={`w-2 h-2 mt-2 rounded-full ${
          activity.type === 'success'
            ? 'bg-green-500'
            : activity.type === 'warning'
              ? 'bg-yellow-500'
              : activity.type === 'error'
                ? 'bg-red-500'
                : 'bg-blue-500'
        }`}
      ></div>
      <div className="flex-1">
        <div className="flex justify-between items-start">
          <div>
            <p className="font-medium text-gray-900 text-sm">
              {viewType === 'corporate' ? activity.company : activity.guest}
            </p>
            <p className="text-xs text-gray-600">{activity.action}</p>
          </div>
          <div className="text-right">
            <p className="font-medium text-gray-900 text-sm">{activity.amount}</p>
            <p className="text-xs text-gray-500">{activity.time}</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Stat cards configuration based on view type
  const getStatCards = () => {
    if (viewType === 'corporate') {
      return [
        { title: 'Total Companies', value: dashboardStats.totalCompanies, change: '+12%' },
        { title: 'Active Contracts', value: dashboardStats.activeContracts, change: '+8%' },
        { title: 'Pending Verification', value: dashboardStats.pendingVerifications, change: '+3' },
        { title: 'Total Revenue', value: dashboardStats.totalRevenue, change: '+15%' },
        { title: 'Average Spending', value: dashboardStats.averageSpending, change: '-5%' },
        { title: 'Renewals This Month', value: dashboardStats.renewalThisMonth, change: '+2' },
        { title: 'High Risk Accounts', value: dashboardStats.highRiskAccounts, change: '-1' },
      ];
    } else {
      // For leisure view - you can implement similar backend APIs
      return [
        { title: 'Total Guests', value: '0', change: '+0%' },
        { title: 'Active Bookings', value: '0', change: '+0%' },
        { title: 'Pending Approvals', value: '0', change: '+0' },
        { title: 'Total Spend', value: '₹0', change: '+0%' },
        { title: 'Average Spend', value: '₹0', change: '+0%' },
        { title: 'Repeat Guests', value: '0', change: '+0%' },
        { title: 'VIP Guests', value: '0', change: '+0' },
      ];
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header Section */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Customer Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/corporate-overview')}
            className="btn text-black font-bold"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/leisure')}
            className="btn"
          >
            Guest Management
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/corporate')}
            className="btn"
          >
            Corporate Accounts
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/contracts')}
            className="btn"
          >
            Contracts
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/credit-verification')}
            className="btn"
          >
            Credit & Verification
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/settings')}
            className="btn "
          >
            Settings
          </button>
        </div>
      </header>

      {/* View Type Toggle */}
      <div className="flex justify-between items-center mb-6 px-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {viewType === 'corporate' ? 'Corporate Overview' : 'Leisure Guests Overview'}
          </h1>
          <p className="text-gray-600 text-sm">
            {viewType === 'corporate'
              ? 'Manage and monitor corporate accounts'
              : 'Manage and monitor leisure guests'}
          </p>
        </div>

        <div className="flex items-center space-x-4">
          {/* View Type Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => handleViewTypeChange('corporate')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewType === 'corporate'
                  ? 'bg-white shadow-sm text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FaBuilding className="text-sm" />
              <span>Corporate</span>
            </button>
            <button
              onClick={() => handleViewTypeChange('leisure')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewType === 'leisure'
                  ? 'bg-white shadow-sm text-green-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FaUmbrellaBeach className="text-sm" />
              <span>Leisure</span>
            </button>
          </div>

          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg "
            />
          </div>
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <FaFilter className="text-gray-600" />
            <span>Filter</span>
          </button>

          {/* Refresh Button */}
          <button
            onClick={fetchAllData}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <FaSync className={`text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 px-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-lg font-bold text-gray-900">
            {viewType === 'corporate' ? '98%' : '94%'}
          </div>
          <div className="text-gray-600 text-sm">
            {viewType === 'corporate' ? 'Customer Satisfaction' : 'Guest Satisfaction'}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-lg font-bold text-gray-900">
            {viewType === 'corporate' ? '47' : '128'}
          </div>
          <div className="text-gray-600 text-sm">
            {viewType === 'corporate' ? 'New This Quarter' : 'New This Quarter'}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-lg font-bold text-gray-900">
            {viewType === 'corporate' ? '92%' : '88%'}
          </div>
          <div className="text-gray-600 text-sm">
            {viewType === 'corporate' ? 'Retention Rate' : 'Repeat Guest Rate'}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-lg font-bold text-gray-900">
            {viewType === 'corporate' ? '15 Days' : '2.3 Days'}
          </div>
          <div className="text-gray-600 text-sm">
            {viewType === 'corporate' ? 'Avg. Onboarding Time' : 'Avg. Stay Duration'}
          </div>
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-7 gap-1 mb-6 mt-4 px-4">
        {getStatCards().map((stat, index) => (
          <StatCard key={index} title={stat.title} value={stat.value} change={stat.change} />
        ))}
      </div>

      {/* Charts and Detailed Metrics */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6 px-4">
        {/* Revenue Chart */}
        <div className="xl:col-span-2 bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-900">
              {viewType === 'corporate' ? 'Revenue Trend' : 'Booking Revenue Trend'}
            </h3>
            <select
              value={timeRange}
              onChange={(e) => handleTimeRangeChange(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
              <option value="quarter">Last Quarter</option>
              <option value="year">Last Year</option>
            </select>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke={viewType === 'corporate' ? '#3b82f6' : '#10b981'}
                  fill={viewType === 'corporate' ? '#3b82f6' : '#10b981'}
                  fillOpacity={0.1}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-300 flex items-center justify-center text-gray-500">
              No revenue data available
            </div>
          )}
        </div>

        {/* Top Companies/Guests */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 mb-4">
            {viewType === 'corporate' ? 'Top Companies by Spending' : 'Top Guests by Spend'}
          </h3>
          <div className="space-y-3">
            {topItems.length > 0 ? (
              topItems.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-6 h-6 rounded flex items-center justify-center font-bold text-xs ${
                        viewType === 'corporate'
                          ? 'bg-blue-100 text-blue-600'
                          : 'bg-green-100 text-green-600'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{item.name}</p>
                      <p className="text-xs text-gray-500">
                        {viewType === 'corporate' ? `Spend: ${item.spend}` : `Spend: ${item.spend}`}
                      </p>
                    </div>
                  </div>
                  <div
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      viewType === 'corporate'
                        ? item.status === 'VIP'
                          ? 'bg-purple-100 text-purple-800'
                          : item.status === 'Premium'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        : item.status === 'VIP'
                          ? 'bg-purple-100 text-purple-800'
                          : item.status === 'Premium'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {viewType === 'corporate' ? item.status : item.status}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">No data available</div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 px-4">
        {/* Recent Activity */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Recent Activity</h3>
          </div>
          <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
            {activities.length > 0 ? (
              activities.map((activity) => <ActivityItem key={activity.id} activity={activity} />)
            ) : (
              <div className="text-center text-gray-500 py-8">No recent activities</div>
            )}
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-4">
              {viewType === 'corporate' ? 'Contracts Overview' : 'Bookings Overview'}
            </h3>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar
                    dataKey={viewType === 'corporate' ? 'contracts' : 'bookings'}
                    fill={viewType === 'corporate' ? '#10b981' : '#3b82f6'}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-200 flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-4">
              {viewType === 'corporate' ? 'Verifications Trend' : 'Approvals Trend'}
            </h3>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey={viewType === 'corporate' ? 'verifications' : 'approvals'}
                    stroke={viewType === 'corporate' ? '#8b5cf6' : '#f59e0b'}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-200 flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorporateOverview;
